import { useState, useEffect, useRef } from 'react';
import { X, Mail, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';

interface EmailCodeVerifyModalProps {
  isOpen: boolean;
  email: string;
  onClose: () => void;
  onVerify: (code: string) => Promise<boolean>;
  onResend: () => Promise<{ error: string | null }>;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(local.length - 2, 3))}@${domain}`;
}

export default function EmailCodeVerifyModal({
  isOpen,
  email,
  onClose,
  onVerify,
  onResend,
}: EmailCodeVerifyModalProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      setCode(['', '', '', '', '', '']);
      setError('');
      setResendSuccess(false);
      setCountdown(0);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  if (!isOpen) return null;

  const handleDigit = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[index] = digit;
    setCode(next);
    setError('');
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    // Auto-submit when last digit filled
    if (digit && index === 5) {
      const fullCode = [...next].join('');
      if (fullCode.length === 6) handleVerify(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const digits = pasted.split('');
      setCode(digits);
      inputRefs.current[5]?.focus();
      handleVerify(pasted);
    }
  };

  const handleVerify = async (fullCode?: string) => {
    const codeStr = fullCode ?? code.join('');
    if (codeStr.length !== 6) {
      setError('Veuillez saisir les 6 chiffres');
      return;
    }
    setLoading(true);
    setError('');
    const ok = await onVerify(codeStr);
    if (!ok) {
      setError('Code incorrect ou expiré. Veuillez réessayer.');
      setCode(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    const { error } = await onResend();
    setResending(false);
    if (error) {
      setError(error);
    } else {
      setResendSuccess(true);
      setCountdown(60);
      setCode(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="relative bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5">
          <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <ShieldCheck size={18} className="text-indigo-300" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white tracking-tight">Vérification par email</h2>
                <p className="text-xs text-slate-400 mt-0.5">Authentification à deux facteurs</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">

          {/* Info */}
          <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
            <Mail size={16} className="text-indigo-500 mt-0.5 shrink-0" />
            <p className="text-sm text-indigo-700">
              Un code à 6 chiffres a été envoyé à <span className="font-semibold">{maskEmail(email)}</span>. Il expire dans 10 minutes.
            </p>
          </div>

          {/* Code inputs */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Code de vérification
            </label>
            <div className="flex gap-2 justify-center" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleDigit(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  disabled={loading}
                  className="w-11 h-12 text-center text-lg font-bold border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors disabled:opacity-50 bg-slate-50"
                />
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Resend success */}
          {resendSuccess && !error && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-700 text-sm">
              Nouveau code envoyé !
            </div>
          )}

          {/* Verify button */}
          <button
            onClick={() => handleVerify()}
            disabled={loading || code.join('').length !== 6}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? 'Vérification…' : 'Vérifier le code'}
          </button>

          {/* Resend */}
          <div className="text-center">
            <span className="text-xs text-slate-400">Vous n'avez pas reçu le code ? </span>
            {countdown > 0 ? (
              <span className="text-xs text-slate-400">Renvoyer dans {countdown}s</span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 transition-colors disabled:opacity-50"
              >
                {resending && <Loader2 size={11} className="animate-spin" />}
                {resending ? 'Envoi…' : (
                  <>
                    <RefreshCw size={11} />
                    Renvoyer
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
